import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import {
  Button,
  Typography,
  Popover,
  Box,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { Globe } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { axiosInstance } from '../../../assets/utils/axiosInstance';
import { ROLE_VALUES } from '../../../constants/customerConstant';
import { useLanguage } from '../../../contexts/lang/languageContext';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import { Language } from '../../../interfaces/Language';
import { setRoles } from '../../../slices/userRolesSlice';
import { AppDispatch, RootState } from '../../../utils/store';

interface LanguageSelectorProps {
  //ts-ignore
  variant: 'dark' | 'light';
}

const LanguageSelector = ({ variant }: LanguageSelectorProps) => {
  const languageOptionsRef = useRef(null);
  const [languageOptionsOpen, setLanguageOptionsOpen] = useState(false);
  const userType = localStorage.getItem('user_type');
  const { createToastNotification } = useToastNotificationContext();
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();

  const tenantLanguages = useSelector(
    (state: RootState) => state.tenant.languages,
  );

  const { selectedLanguage, setLanguage } = useLanguage();

  const handleCloseLanguageOptions = () => {
    setLanguageOptionsOpen(false);
  };

  const handleLanguageOptionsOpen = () => {
    setLanguageOptionsOpen(true);
  };

  const handleLanguageChange = async (lang: Language) => {
    const userLang = tenantLanguages.find(
      (language: Language) => language.code === lang.code,
    );
    if (userLang) {
      try {
        if (
          userType === `${ROLE_VALUES.TENANT_ADMIN}` ||
          userType === `${ROLE_VALUES.PARTNER_ADMIN}` ||
          userType === `${ROLE_VALUES.TENANT_USER}` ||
          userType === `${ROLE_VALUES.PARTNER_USER}`
        ) {
          const response = await axiosInstance().patch('/user/flags', {
            language_id: userLang.id,
          });

          dispatch(setRoles(response.data));
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Language changed successfully',
            }),
            'success',
          );
        }
        if (userType === `${ROLE_VALUES.WHISTLE_BLOWER}`) {
          await axiosInstance().patch('/flags', {
            language_id: userLang.id,
          });

          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Language changed successfully',
            }),
            'success',
          );
        }
      } catch (error) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'Error occurred while changing language',
          }),
          'error',
        );
      }
    }
    setLanguage(lang);
    handleCloseLanguageOptions();
  };

  const languageSelectorBgColor = () => {
    return variant === 'dark' ? '#292929' : 'inherit';
  };

  const LanguageOptionsPopover = () => {
    return (
      <Popover
        open={languageOptionsOpen}
        anchorEl={languageOptionsRef.current}
        onClose={handleCloseLanguageOptions}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          pointerEvents: 'auto',
        }}
        slotProps={{
          paper: {},
        }}
        disableScrollLock
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            borderRadius: '6px',
            backgroundColor: '#FFF',
            py: '4px',
          }}
        >
          <Box
            sx={{
              py: '4px',
              width: '180px',
              color: '#878787',
            }}
          >
            {tenantLanguages.map((language, index) => (
              <MenuItem
                key={index}
                value={language.code}
                onClick={() => handleLanguageChange(language)}
              >
                <ListItemIcon>
                  {language.code === selectedLanguage.code ? (
                    <CheckIcon sx={{ color: '#878787' }} />
                  ) : (
                    ''
                  )}
                </ListItemIcon>
                <ListItemText>
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    {language.name}
                  </Typography>
                </ListItemText>
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Popover>
    );
  };

  if (tenantLanguages.length === 0) {
    return null;
  }

  return (
    <Box>
      <Button
        ref={languageOptionsRef}
        disableRipple
        variant='outlined'
        sx={{
          display: 'flex',
          float: 'right',
          justifyContent: 'space-between',
          backgroundColor: languageSelectorBgColor(),
          p: '8px, 12px, 8px, 12px',
          border: 'none',
          '&:hover': {
            backgroundColor: languageSelectorBgColor(),
            border: 'none',
          },
          '&:active': {
            backgroundColor: languageSelectorBgColor(),
            border: 'none',
          },
        }}
        onClick={handleLanguageOptionsOpen}
        startIcon={<Globe color='white' />}
        endIcon={
          <ArrowDropDownIcon
            style={{
              justifyContent: 'right',
              color: '#878787',
              transform: `rotate(${languageOptionsOpen ? 180 : 0}deg)`,
            }}
            fontSize='small'
          />
        }
      >
        <Typography
          sx={{
            color: 'white',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            textTransform: 'none',
            fontWeight: 500,
            ml: 1,
          }}
        >
          {
            tenantLanguages.find(
              (language) => language.code === selectedLanguage.code,
            )?.name
          }
        </Typography>
      </Button>
      <LanguageOptionsPopover />
    </Box>
  );
};

export default LanguageSelector;
